import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, CardBody, CardTitle, CardText,
    Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input,
    Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import { useSelector } from 'react-redux';
import { USERTYPES } from 'components/Common/Constant';
import classnames from 'classnames';
import axiosInstance from 'components/Common/ApiManagement/axiosInstance';
import { ApiNames } from 'components/Common/ApiManagement/apiServices';
import { showToaster, handleErrors } from 'components/Common/common';

const HelpSupport = () => {
    document.title = "Help & Support | Gov-Compliance";

    const user = useSelector((state) => state?.users.users) || {};
    const userRole = user.userType || user.UserType;
    const isAdmin = userRole === USERTYPES.Admin || userRole === USERTYPES.Global || userRole === USERTYPES.Organization;

    const [activeTab, setActiveTab] = useState('1');
    const [uploadModal, setUploadModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteIdeaModal, setDeleteIdeaModal] = useState(false);

    const [videos, setVideos] = useState([]);
    const [ideas, setIdeas] = useState([]);

    const [newIdea, setNewIdea] = useState("");
    const [videoTitle, setVideoTitle] = useState("");
    const [videoDesc, setVideoDesc] = useState("");
    const [videoFile, setVideoFile] = useState(null);

    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedIdea, setSelectedIdea] = useState(null);

    // Fetch Data
    const fetchData = async () => {
        try {
            const [vidRes, ideaRes] = await Promise.all([
                axiosInstance.get(ApiNames.HELP_SUPPORT_VIDEOS),
                axiosInstance.get(ApiNames.HELP_SUPPORT_IDEAS)
            ]);
            if (vidRes.data.status === 1) setVideos(vidRes.data.data.videos);
            if (ideaRes.data.status === 1) setIdeas(ideaRes.data.data.ideas);
        } catch (error) {
            console.error("Error fetching help data", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const toggleTab = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const toggleUploadModal = () => {
        setUploadModal(!uploadModal);
        if (uploadModal) resetVideoForm();
    };

    const toggleEditModal = () => setEditModal(!editModal);
    const toggleDeleteModal = () => setDeleteModal(!deleteModal);
    const toggleDeleteIdeaModal = () => setDeleteIdeaModal(!deleteIdeaModal);

    const resetVideoForm = () => {
        setVideoTitle("");
        setVideoDesc("");
        setVideoFile(null);
        setSelectedVideo(null);
    };

    const handleIdeaSubmit = async (e) => {
        e.preventDefault();
        if (!newIdea.trim()) return;
        try {
            const res = await axiosInstance.post(ApiNames.HELP_SUPPORT_IDEA_ADD, { content: newIdea });
            if (res.data.status === 1) {
                showToaster(1, res.data.message || "Idea submitted successfully");
                setNewIdea("");
                fetchData();
            } else {
                showToaster(0, res.data.message || "Failed to submit idea");
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const updateIdeaStatus = async (id, status) => {
        try {
            const res = await axiosInstance.put(ApiNames.HELP_SUPPORT_IDEA_UPDATE(id), { status });
            if (res.data.status === 1) {
                showToaster(1, res.data.message || "Idea status updated");
                fetchData();
            } else {
                showToaster(0, res.data.message || "Failed to update idea status");
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const openDeleteIdeaModal = (idea) => {
        setSelectedIdea(idea);
        toggleDeleteIdeaModal();
    };

    const handleDeleteIdeaSubmit = async () => {
        try {
            const res = await axiosInstance.delete(ApiNames.HELP_SUPPORT_IDEA_DELETE(selectedIdea._id));
            if (res.data.status === 1) {
                showToaster(1, res.data.message || "Idea deleted successfully");
                toggleDeleteIdeaModal();
                fetchData();
            } else {
                showToaster(0, res.data.message || "Failed to delete idea");
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const handleVideoSubmit = async (e) => {
        e.preventDefault();
        if (!videoFile) {
            showToaster(0, "Please select a video file");
            return;
        }

        const formData = new FormData();
        formData.append("title", videoTitle);
        formData.append("description", videoDesc);
        formData.append("videoFile", videoFile);

        try {
            const res = await axiosInstance.post(ApiNames.HELP_SUPPORT_VIDEO_UPLOAD, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status === 1) {
                showToaster(1, res.data.message || "Video uploaded successfully");
                toggleUploadModal();
                fetchData();
            } else {
                showToaster(0, res.data.message || "Failed to upload video");
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const openEditModal = (video) => {
        setSelectedVideo(video);
        setVideoTitle(video.title);
        setVideoDesc(video.description || "");
        toggleEditModal();
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("title", videoTitle);
        formData.append("description", videoDesc);

        if (videoFile) {
            formData.append("videoFile", videoFile);
        }

        try {
            const res = await axiosInstance.put(ApiNames.HELP_SUPPORT_VIDEO_EDIT(selectedVideo._id), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.status === 1) {
                showToaster(1, res.data.message || "Video updated successfully");
                toggleEditModal();
                fetchData();
            } else {
                showToaster(0, res.data.message || "Failed to update video");
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const openDeleteModal = (video) => {
        setSelectedVideo(video);
        toggleDeleteModal();
    };

    const handleDeleteSubmit = async () => {
        try {
            const res = await axiosInstance.delete(ApiNames.HELP_SUPPORT_VIDEO_DELETE(selectedVideo._id));
            if (res.data.status === 1) {
                showToaster(1, res.data.message || "Video deleted successfully");
                toggleDeleteModal();
                fetchData();
            } else {
                showToaster(0, res.data.message || "Failed to delete video");
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    // To serve locally stored videos safely over backend
    const getVideoUrl = (urlPath) => {
        if (!urlPath) return "";
        if (urlPath.startsWith("http")) return urlPath;
        // Construct full backend path
        return `${process.env.REACT_APP_API_URL.replace('/api', '')}${urlPath}`;
    };

    return (
        <React.Fragment>
            <div className="page-content" style={{ marginTop: '-20px' }}>
                <Container fluid>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="card-title text-primary font-size-18">Help & Support</h4>
                        {isAdmin && (
                            <Button color="primary" onClick={toggleUploadModal}>
                                <i className="mdi mdi-upload me-1"></i> Upload Tutorial Video
                            </Button>
                        )}
                    </div>

                    <Nav tabs className="nav-tabs-custom mb-4">
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '1' })}
                                onClick={() => { toggleTab('1'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="mdi mdi-video-outline me-1"></i> Tutorial Videos
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '2' })}
                                onClick={() => { toggleTab('2'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="mdi mdi-lightbulb-on-outline me-1"></i> Submit an Idea
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                            <Row className="mt-4">
                                {videos.length > 0 ? videos.map(video => (
                                    <Col xl="4" sm="6" key={video._id} className="mb-4">
                                        <Card className="shadow-sm border-0 rounded overflow-hidden" style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.classList.add('shadow-lg'); }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.classList.remove('shadow-lg'); }}
                                        >
                                            <div className="ratio ratio-16x9 bg-dark">
                                                <video controls src={getVideoUrl(video.videoUrl)} style={{ objectFit: 'contain' }}></video>
                                            </div>
                                            <CardBody className="p-4">
                                                <div className="d-flex align-items-center mb-2">
                                                    <span className="badge bg-soft-primary text-primary font-size-11 px-2 py-1 rounded-pill">Tutorial</span>
                                                </div>
                                                <CardTitle className="h5 mb-2 fw-bold">{video.title}</CardTitle>
                                                <CardText className="text-muted font-size-14 mb-4">{video.description}</CardText>

                                                {isAdmin && (
                                                    <div className="d-flex justify-content-end gap-2 border-top pt-3">
                                                        <Button color="light" size="sm" className="btn-rounded px-3" onClick={() => openEditModal(video)}>
                                                            <i className="mdi mdi-pencil me-1"></i> Edit
                                                        </Button>
                                                        <Button color="soft-danger" size="sm" className="btn-rounded px-3 text-danger border-0" onClick={() => openDeleteModal(video)}>
                                                            <i className="mdi mdi-delete me-1"></i> Delete
                                                        </Button>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                )) : (
                                    <Col xs="12">
                                        <div className="text-center text-muted p-5 bg-light rounded">
                                            <i className="mdi mdi-video-off-outline font-size-24 mb-2"></i>
                                            <h5>No tutorial videos available yet.</h5>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </TabPane>
                        <TabPane tabId="2">
                            <Row className="mt-4">
                                <Col lg="6">
                                    <Card className="shadow-sm mb-4 border">
                                        <CardBody>
                                            <CardTitle className="h5 mb-4 text-primary">Share Your Idea</CardTitle>
                                            <Form onSubmit={handleIdeaSubmit}>
                                                <FormGroup className="mb-3">
                                                    <Label for="ideaContent">What would you like to see improved or added?</Label>
                                                    <Input
                                                        type="textarea"
                                                        id="ideaContent"
                                                        rows="4"
                                                        placeholder="Describe your idea or feature request here..."
                                                        value={newIdea}
                                                        onChange={(e) => setNewIdea(e.target.value)}
                                                    />
                                                </FormGroup>
                                                <Button type="submit" color="success">Submit Idea</Button>
                                            </Form>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col lg="6">
                                    <Card className="shadow-sm border">
                                        <CardBody>
                                            <CardTitle className="h5 mb-4 text-primary">Recent Ideas</CardTitle>
                                            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                <table className="table table-hover mb-0 align-middle">
                                                    <tbody>
                                                        {ideas.length > 0 ? ideas.map((idea, idx) => (
                                                            <tr key={idea._id || idx}>
                                                                <td>
                                                                    <div className="font-size-14">{idea.content}</div>
                                                                    <div className="text-muted font-size-12 mt-1">
                                                                        {new Date(idea.createdAt).toLocaleDateString()}
                                                                    </div>
                                                                </td>
                                                                <td style={{ width: '120px' }}>
                                                                    <span className={`badge ${idea.status === 'Pending' ? 'bg-warning' : idea.status === 'Cancelled' ? 'bg-danger' : 'bg-info'} font-size-12 px-2 py-1`}>
                                                                        {idea.status}
                                                                    </span>
                                                                </td>
                                                                {isAdmin && (
                                                                    <td style={{ width: '150px' }}>
                                                                        <div className="d-flex gap-2">
                                                                            {idea.status !== 'Under Review' && idea.status !== 'Completed' && idea.status !== 'Cancelled' && (
                                                                                <Button color="soft-info" size="sm" onClick={() => updateIdeaStatus(idea._id, 'Under Review')} title="Mark as Under Review">
                                                                                    <i className="mdi mdi-eye-check"></i>
                                                                                </Button>
                                                                            )}
                                                                            {idea.status !== 'Cancelled' && idea.status !== 'Completed' && (
                                                                                <Button color="soft-warning" size="sm" onClick={() => updateIdeaStatus(idea._id, 'Cancelled')} title="Cancel Idea">
                                                                                    <i className="mdi mdi-close-circle"></i>
                                                                                </Button>
                                                                            )}
                                                                            <Button color="soft-danger" size="sm" onClick={() => openDeleteIdeaModal(idea)} title="Delete Idea">
                                                                                <i className="mdi mdi-delete-outline"></i>
                                                                            </Button>
                                                                        </div>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        )) : (
                                                            <tr>
                                                                <td colSpan={isAdmin ? "3" : "2"} className="text-center text-muted py-4">No ideas submitted yet.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>
                    </TabContent>
                </Container>
            </div>

            {/* Admin Upload Modal */}
            <Modal isOpen={uploadModal} toggle={toggleUploadModal} centered size="lg">
                <ModalHeader toggle={toggleUploadModal}>Upload Tutorial Video</ModalHeader>
                <ModalBody>
                    <Form id="videoUploadForm" onSubmit={handleVideoSubmit}>
                        <FormGroup className="mb-3">
                            <Label for="videoTitle">Title <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="videoTitle"
                                placeholder="Enter video title"
                                required
                                value={videoTitle}
                                onChange={(e) => setVideoTitle(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label for="videoDesc">Description</Label>
                            <Input
                                type="textarea"
                                id="videoDesc"
                                rows="3"
                                placeholder="Enter short description"
                                value={videoDesc}
                                onChange={(e) => setVideoDesc(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label for="videoFile">Video File (MP4) <span className="text-danger">*</span></Label>
                            <Input
                                type="file"
                                id="videoFile"
                                accept="video/mp4,video/x-m4v,video/*"
                                onChange={(e) => setVideoFile(e.target.files[0])}
                                required
                            />
                            <small className="form-text text-muted">Please upload a valid .mp4 file. Max duration ~10 minutes.</small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleUploadModal}>Cancel</Button>
                    <Button color="primary" type="submit" form="videoUploadForm">Upload Video</Button>
                </ModalFooter>
            </Modal>

            {/* Admin Edit Modal */}
            <Modal isOpen={editModal} toggle={toggleEditModal} centered>
                <ModalHeader toggle={toggleEditModal}>Edit Video Details</ModalHeader>
                <ModalBody>
                    <Form id="videoEditForm" onSubmit={handleEditSubmit}>
                        <FormGroup className="mb-3">
                            <Label for="editVideoTitle">Title <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                id="editVideoTitle"
                                placeholder="Enter video title"
                                required
                                value={videoTitle}
                                onChange={(e) => setVideoTitle(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label for="editVideoDesc">Description</Label>
                            <Input
                                type="textarea"
                                id="editVideoDesc"
                                rows="3"
                                placeholder="Enter short description"
                                value={videoDesc}
                                onChange={(e) => setVideoDesc(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label for="editVideoFile">Replace Video File (Optional)</Label>
                            <Input
                                type="file"
                                id="editVideoFile"
                                accept="video/mp4,video/x-m4v,video/*"
                                onChange={(e) => setVideoFile(e.target.files[0])}
                            />
                            <small className="form-text text-muted">Upload a new file to replace the current video. Leave blank to keep existing video.</small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleEditModal}>Cancel</Button>
                    <Button color="primary" type="submit" form="videoEditForm">Save Changes</Button>
                </ModalFooter>
            </Modal>

            {/* Admin Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
                <ModalHeader toggle={toggleDeleteModal} className="border-0 pb-0"></ModalHeader>
                <ModalBody className="text-center pt-0 px-4">
                    <div className="mb-4">
                        <i className="mdi mdi-alert-circle-outline text-danger" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h4 className="mb-2">Are you sure?</h4>
                    <p className="text-muted">
                        Do you really want to delete the video <strong>"{selectedVideo?.title}"</strong>? This action cannot be undone.
                    </p>
                </ModalBody>
                <ModalFooter className="justify-content-center border-0 pt-0">
                    <Button color="secondary" className="px-4" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" className="px-4" onClick={handleDeleteSubmit}>Yes, Delete It</Button>
                </ModalFooter>
            </Modal>

            {/* Admin Delete Idea Confirmation Modal */}
            <Modal isOpen={deleteIdeaModal} toggle={toggleDeleteIdeaModal} centered>
                <ModalHeader toggle={toggleDeleteIdeaModal} className="border-0 pb-0"></ModalHeader>
                <ModalBody className="text-center pt-0 px-4">
                    <div className="mb-4">
                        <i className="mdi mdi-alert-circle-outline text-danger" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h4 className="mb-2">Are you sure?</h4>
                    <p className="text-muted">
                        Do you really want to delete this idea? This action cannot be undone.
                    </p>
                </ModalBody>
                <ModalFooter className="justify-content-center border-0 pt-0">
                    <Button color="secondary" className="px-4" onClick={toggleDeleteIdeaModal}>Cancel</Button>
                    <Button color="danger" className="px-4" onClick={handleDeleteIdeaSubmit}>Yes, Delete It</Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default HelpSupport;
